import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowRight, Sparkles, RefreshCw, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";

export default function Account() {
    const [email, setEmail] = useState("");
    const [searched, setSearched] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [resentId, setResentId] = useState(null);

    const lookup = async (e) => {
        e?.preventDefault();
        setLoading(true);
        setError("");
        setResentId(null);
        try {
            const r = await api.get(`/account/orders?email=${encodeURIComponent(email)}`);
            setData(r.data);
            setSearched(true);
        } catch (err) {
            const d = err.response?.data?.detail;
            setError(typeof d === "string" ? d : "Could not look up your orders");
        }
        setLoading(false);
    };

    const resend = async (orderId) => {
        setResentId(null);
        setError("");
        try {
            await api.post(`/account/resend-receipt/${orderId}`);
            setResentId(orderId);
            setTimeout(() => setResentId(null), 4000);
        } catch (err) {
            const d = err.response?.data?.detail;
            setError(typeof d === "string" ? d : "Failed to resend receipt");
        }
    };

    return (
        <div className="min-h-[80vh]" data-testid="account-page">
            <section className="max-w-3xl mx-auto px-6 lg:px-12 pt-16 pb-10">
                <p className="text-xs uppercase tracking-[0.3em] text-femcare-accent mb-4">Your account</p>
                <h1 className="editorial-headline text-4xl md:text-5xl lg:text-6xl text-femcare-purple mb-6">
                    Welcome back.<br/>
                    <span className="italic font-light">Find your subscription.</span>
                </h1>
                <p className="text-femcare-purple/70 text-lg leading-relaxed mb-10">
                    Enter the email you used at checkout. We'll show your active subscriptions and let you re-send any receipt.
                </p>

                <form onSubmit={lookup} className="flex flex-col sm:flex-row gap-3" data-testid="account-lookup-form">
                    <div className="flex-1 relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-femcare-accent" />
                        <input
                            type="email" required value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@example.com"
                            className="w-full bg-white border border-femcare-border rounded-full pl-11 pr-4 py-4 focus:outline-none focus:border-femcare-pink text-femcare-purple"
                            data-testid="account-email-input"
                        />
                    </div>
                    <button type="submit" disabled={loading} className="btn-primary justify-center disabled:opacity-50" data-testid="account-lookup-btn">
                        {loading ? "Searching..." : "Find my orders"}
                        <ArrowRight className="w-4 h-4" />
                    </button>
                </form>

                {error && <p className="text-sm text-red-600 mt-4" data-testid="account-error">{error}</p>}
            </section>

            {searched && data && (
                <section className="max-w-3xl mx-auto px-6 lg:px-12 pb-24">
                    {data.count === 0 ? (
                        <div className="bg-femcare-petal/40 border border-femcare-border rounded-3xl p-10 text-center" data-testid="account-empty">
                            <Sparkles className="w-10 h-10 text-femcare-pink mx-auto mb-4" />
                            <h3 className="font-serif text-2xl text-femcare-purple mb-3">No orders found</h3>
                            <p className="text-femcare-purple/70 mb-6">
                                We couldn't find any paid orders for <strong>{data.email}</strong>.
                            </p>
                            <Link to="/pricing" className="btn-primary">
                                See plans <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center gap-3 mb-6">
                                <CheckCircle2 className="w-5 h-5 text-femcare-pink" />
                                <p className="text-sm text-femcare-purple/70">
                                    Found <strong>{data.count}</strong> paid order{data.count > 1 ? "s" : ""} for <strong>{data.email}</strong>
                                </p>
                            </div>

                            <div className="space-y-4">
                                {data.orders.map((o) => (
                                    <div key={o.id} className="bg-white border border-femcare-border rounded-2xl p-6" data-testid={`account-order-${o.id}`}>
                                        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                                            <div className="flex-1">
                                                <p className="text-xs uppercase tracking-[0.3em] text-femcare-accent mb-2">
                                                    Paid · {(o.paid_at || "").slice(0, 10)}
                                                </p>
                                                <div className="space-y-1 mb-3">
                                                    {(o.items || []).map((i, k) => (
                                                        <p key={k} className="font-serif text-lg text-femcare-purple">
                                                            {i.name} <span className="text-femcare-purple/50 text-sm">/{i.period}</span>
                                                        </p>
                                                    ))}
                                                </div>
                                                <p className="font-mono text-xs text-femcare-purple/50">{o.receipt}</p>
                                            </div>
                                            <div className="text-left md:text-right">
                                                <p className="font-serif text-3xl text-femcare-pink mb-3">₹{o.amount_inr}</p>
                                                <button
                                                    onClick={() => resend(o.id)}
                                                    className="text-xs uppercase tracking-wider text-femcare-accent hover:text-femcare-pink inline-flex items-center gap-1.5 border border-femcare-border rounded-full px-4 py-2 hover:border-femcare-pink transition"
                                                    data-testid={`resend-receipt-${o.id}`}
                                                >
                                                    <RefreshCw className="w-3 h-3" />
                                                    {resentId === o.id ? "Sent ✓" : "Resend receipt"}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </section>
            )}
        </div>
    );
}
